import React, { useState } from 'react';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaLinkedin, FaPaperPlane, FaClock } from 'react-icons/fa';
import './ContactPage.css';

interface FormData {
    name: string;
    email: string;
    company: string;
    service: string;
    message: string;
}

const ContactPage: React.FC = () => {
    const [formData, setFormData] = useState<FormData>({
        name: '', email: '', company: '', service: '', message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [submitError, setSubmitError] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitMessage('');
        setSubmitError(false);

        try {
            const emailjs = (await import('@emailjs/browser')).default;
            await emailjs.send('service_k5csjit', 'template_hcstncb', {
                from_name: formData.name,
                from_email: formData.email,
                from_company: formData.company,
                service: formData.service,
                message: formData.message,
                to_email: 'support@Corenuvate.com',
            }, { publicKey: 'wtAqmUvZdf-2ZdHQP' });
            setSubmitMessage("Thank you! We'll get back to you within 24 hours.");
            setFormData({ name: '', email: '', company: '', service: '', message: '' });
        } catch (error) {
            console.error('Email error:', error);
            setSubmitMessage('Something went wrong. Please try again or email us directly.');
            setSubmitError(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const contactInfo = [
        { icon: <FaMapMarkerAlt />, label: 'Office Address', value: 'Ary Meuldijkstraat 6\n3151 JC Hoek van Holland, NL' },
        { icon: <FaEnvelope />, label: 'Email', value: 'support@corenuvate.com', href: 'mailto:support@corenuvate.com' },
        { icon: <FaPhone />, label: 'Phone', value: '+31 627 877 227', href: 'tel:+31627877227' },
        { icon: <FaClock />, label: 'Business Hours', value: 'Mon – Fri · 9:00 – 18:00 CET' },
    ];

    const services = [
        '-- Select a Service --',
        'Microsoft 365 & Power Platform',
        'Web Development & Modern UI',
        'Database Design & Management',
        'Mobile App Development',
        'API Development & Integration',
        'Digital Modernisation & Cloud',
        'Process Automation & AI',
        'Testing & Application Management',
        'General Inquiry',
    ];

    return (
        <main className="contact-page">
            {/* Banner */}
            <section className="page-banner">
                <div className="banner-bg"><div className="banner-glow" /></div>
                <div className="container">
                    <span className="page-banner-tag">Get In Touch</span>
                    <h1 className="page-banner-title fade-in-up">
                        Let's Build Something<br /><span className="gradient-text">Great Together</span>
                    </h1>
                    <p className="page-banner-subtitle fade-in-up delay-1">
                        Ready to start your digital transformation? Reach out and we'll get back to you within one business day.
                    </p>
                </div>
            </section>

            {/* Contact Section */}
            <section className="section">
                <div className="container">
                    <div className="contact-layout">
                        {/* Left: Info */}
                        <div className="contact-info-col">
                            <h2 className="section-title">Contact <span>Information</span></h2>
                            <p style={{ color: '#64748B', lineHeight: 1.7, marginBottom: '24px', fontSize: '0.95rem' }}>
                                We're here to help. Reach out through any channel below or fill in the form and we'll respond promptly.
                            </p>

                            {/* Support team image */}
                            <div className="contact-team-img-block">
                                <img src="/images/contact_team.png" alt="CoreNuVate support team" className="contact-team-img" />
                                <div className="contact-team-overlay">
                                    <div className="contact-team-badge">
                                        <span className="team-online-dot" />
                                        <div>
                                            <div className="float-value">Available Now</div>
                                            <div className="float-label">Response within 24 hours</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="contact-info-list">
                                {contactInfo.map((c, i) => (
                                    <div key={i} className="glass-card contact-info-card">
                                        <div className="contact-icon-wrap">{c.icon}</div>
                                        <div>
                                            <div className="contact-info-label">{c.label}</div>
                                            {c.href ? (
                                                <a href={c.href} className="contact-info-value link">{c.value}</a>
                                            ) : (
                                                <div className="contact-info-value" style={{ whiteSpace: 'pre-line' }}>{c.value}</div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Social */}
                            <div className="contact-social">
                                <p className="social-label">Follow us</p>
                                <div className="social-links">
                                    <a href="https://linkedin.com/company/corenuvate" target="_blank" rel="noopener noreferrer" className="social-btn-lg">
                                        <FaLinkedin /> LinkedIn
                                    </a>
                                    <a href="mailto:support@corenuvate.com" className="social-btn-lg">
                                        <FaEnvelope /> Email Us
                                    </a>
                                </div>
                            </div>

                            {/* Legal */}
                            <div className="glass-card legal-card">
                                <div className="legal-row"><span>KVK Number</span><strong>99600293</strong></div>
                                <div className="legal-row"><span>BTW-ID</span><strong>NL005397952B82</strong></div>
                            </div>
                        </div>

                        {/* Right: Form */}
                        <div className="contact-form-col">
                            <div className="glass-card contact-form-card">
                                <h3>Send Us a Message</h3>
                                <p>Fill in the details and we'll get back to you within 24 hours.</p>

                                {submitMessage && (
                                    <div className={`submit-alert ${submitError ? 'error' : 'success'}`}>
                                        {submitMessage}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="contact-form">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="name">Full Name *</label>
                                            <input type="text" id="name" name="name" value={formData.name}
                                                onChange={handleChange} required placeholder="Your full name" />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="email">Email *</label>
                                            <input type="email" id="email" name="email" value={formData.email}
                                                onChange={handleChange} required placeholder="your.email@company.com" />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label htmlFor="company">Company</label>
                                            <input type="text" id="company" name="company" value={formData.company}
                                                onChange={handleChange} placeholder="Your company name" />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="service">Service of Interest</label>
                                            <select id="service" name="service" value={formData.service} onChange={handleChange}>
                                                {services.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="message">Project Details *</label>
                                        <textarea id="message" name="message" value={formData.message}
                                            onChange={handleChange} required rows={5}
                                            placeholder="Tell us about your project — requirements, timeline, budget, and any specific needs..." />
                                    </div>

                                    <button type="submit" className="btn-primary submit-btn" disabled={isSubmitting}>
                                        {isSubmitting ? 'Sending…' : <><FaPaperPlane /> Send Message</>}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default ContactPage;
